import { useState } from 'react';
import { Activity, ArrowRight, CheckCircle2, Loader2, Moon, RotateCw, Unlink, XCircle } from 'lucide-react';
import { User } from '../types';
import { connectStrava, connectOura, disconnectOura, disconnectStrava } from '../services/auth';

interface ServiceConnectionProps {
  user: User;
  onSkip?: () => void;
  showSkip?: boolean;
}

interface ServiceRowProps {
  icon: React.ReactNode;
  iconClasses: string;
  name: string;
  description: string;
  connected: boolean;
  onConnect: () => void;
  onDisconnect: () => Promise<void>;
}

function ServiceRow({ icon, iconClasses, name, description, connected, onConnect, onDisconnect }: ServiceRowProps) {
  const [disconnecting, setDisconnecting] = useState(false);

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await onDisconnect();
      // Tokens live on the user row that App loaded at startup — a reload
      // is the simplest way to refresh every consumer of it.
      window.location.reload();
    } catch (error) {
      console.error(`Failed to disconnect ${name}:`, error);
      setDisconnecting(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl bg-zinc-50 p-4 ring-1 ring-zinc-950/5 dark:bg-zinc-800/60 dark:ring-white/5 sm:p-5">
      <div className="flex items-center gap-3.5">
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${iconClasses}`}>
          {icon}
        </span>
        <div>
          <h3 className="font-semibold text-zinc-900 dark:text-white">{name}</h3>
          <p className="text-sm text-zinc-500 dark:text-zinc-400">{description}</p>
        </div>
      </div>
      {connected ? (
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-600 dark:text-emerald-400">
            <CheckCircle2 className="h-4 w-4" />
            Connected
          </span>
          <button
            type="button"
            onClick={onConnect}
            className="btn-ghost px-2.5"
            title={`Reconnect ${name}`}
            aria-label={`Reconnect ${name}`}
          >
            <RotateCw className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={handleDisconnect}
            disabled={disconnecting}
            className="btn-ghost px-2.5 text-zinc-400 hover:text-rose-600 dark:hover:text-rose-400"
            title={`Disconnect ${name}`}
            aria-label={`Disconnect ${name}`}
          >
            {disconnecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlink className="h-4 w-4" />}
          </button>
        </div>
      ) : (
        <button type="button" onClick={onConnect} className="btn-primary shrink-0">
          Connect
          <ArrowRight className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

export function ServiceConnection({ user, onSkip, showSkip = false }: ServiceConnectionProps) {
  return (
    <div className="card w-full p-6 sm:p-8">
      <h2 className="text-xl font-bold text-zinc-900 dark:text-white">Connect your services</h2>
      <p className="mt-2 text-sm leading-relaxed text-zinc-500 dark:text-zinc-400">
        FitScript gets sharper with real data. Connect your trackers to replace the
        sample recommendation with one built from your actual recovery.
      </p>

      <div className="mt-6 space-y-4">
        <ServiceRow
          icon={<Moon className="h-5 w-5" />}
          iconClasses="bg-violet-600/10 text-violet-600 dark:bg-violet-400/10 dark:text-violet-400"
          name="Oura Ring"
          description="Sleep, HRV & recovery"
          connected={!!user.ouraToken}
          onConnect={() => connectOura()}
          onDisconnect={() => disconnectOura(user.id)}
        />
        <ServiceRow
          icon={<Activity className="h-5 w-5" />}
          iconClasses="bg-amber-600/10 text-amber-600 dark:bg-amber-400/10 dark:text-amber-400"
          name="Strava"
          description="Workouts & training load"
          connected={!!user.stravaToken}
          onConnect={() => connectStrava()}
          onDisconnect={() => disconnectStrava(user.id)}
        />
      </div>

      {showSkip && (
        <div className="mt-6 text-center">
          <button onClick={onSkip} className="btn-ghost mx-auto">
            <XCircle className="h-4 w-4" />
            Skip for now
          </button>
          <p className="mt-1.5 text-xs text-zinc-400">
            You can always connect your services later in settings.
          </p>
        </div>
      )}
    </div>
  );
}
